import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Mail, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const AdminLoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const formatApiErrorDetail = (detail) => {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
      return detail
        .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
        .filter(Boolean)
        .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await login(email, password);
      if (user.role !== "admin") {
        toast.error("Access denied. Admin privileges required.");
        return;
      }
      toast.success("Welcome back!");
      navigate("/admin/dashboard");
    } catch (error) {
      const message = formatApiErrorDetail(error.response?.data?.detail);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFCF9] flex items-center justify-center py-12 px-4" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl md:text-4xl text-spa-neutral-800 italic mb-2">
            Kriel Colon & Beauty
          </h1>
          <p className="text-spa-pink-500 font-medium text-xs tracking-widest uppercase">
            Admin Portal
          </p>
        </div>

        <Card className="rounded-2xl border-0 spa-shadow">
          <CardHeader className="p-8 pb-4 text-center">
            <div className="mx-auto w-16 h-16 bg-spa-pink-100 rounded-full flex items-center justify-center mb-4">
              <Lock className="h-8 w-8 text-spa-pink-500" />
            </div>
            <CardTitle className="font-heading text-2xl text-spa-neutral-800">
              Admin Login
            </CardTitle>
            <p className="text-spa-neutral-500 text-sm mt-2">
              Sign in to manage your spa bookings
            </p>
          </CardHeader>
          <CardContent className="p-8 pt-4">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-spa-neutral-700">
                  Email Address
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-spa-neutral-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="admin@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 rounded-lg border-spa-neutral-200 h-12"
                    required
                    data-testid="admin-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-spa-neutral-700">
                  Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-spa-neutral-400" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 rounded-lg border-spa-neutral-200 h-12"
                    required
                    data-testid="admin-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-spa-neutral-400 hover:text-spa-neutral-600"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-spa-pink hover:bg-spa-pink-600 text-white rounded-lg py-6 text-base font-medium transition-all hover:shadow-lg"
                data-testid="admin-login-btn"
              >
                {loading ? "Signing in..." : "Sign In"}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <a
                href="/"
                className="text-spa-pink-600 hover:text-spa-pink-700 text-sm font-medium transition-colors"
              >
                ← Back to Website
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminLoginPage;
